// Cinematic kill-confirm (roadmap #4): the instant your burst scores a kill,
// the effects clock takes a ~0.6 s breath of slow motion, the chase camera
// eases a few degrees toward the falling victim with a small FOV punch, and a
// banner stamps the confirm. Punctuation, not a cutscene — the player never
// loses control, so ONLY the effects dt is ever dilated (see scale()).

import * as THREE from "three";

export const KILL_CONFIRM = {
  slowSec: 0.6, // effects run at slowScale for this long...
  slowScale: 0.45,
  recoverSec: 0.4, // ...then ease back to 1 over this long
  pullMaxRad: 0.12, // peak camera nudge toward the victim
  fovPunchDeg: 3,
  bannerSec: 1.4,
  cooldownSec: 3, // one cinematic per this window; extra kills just re-stamp
};

const _lookM = new THREE.Matrix4();
const _lookQ = new THREE.Quaternion();
const _fwd = new THREE.Vector3();
const _toV = new THREE.Vector3();

export class KillConfirm {
  constructor() {
    this.at = -Infinity; // trigger time, seconds
    this.victimPos = new THREE.Vector3();
    this.hasVictim = false;
  }

  /** True if a fresh cinematic may start (rate limit); always safe to call. */
  trigger(now, victimPos) {
    if (now - this.at < KILL_CONFIRM.cooldownSec) return false;
    this.at = now;
    this.hasVictim = !!victimPos;
    if (victimPos) this.victimPos.copy(victimPos);
    return true;
  }

  /**
   * Effects time factor. Feed this ONLY to cosmetic updates (particles,
   * tracers, debris) — never the FDM prediction, input sampling, or net
   * interpolation, which must run on the real clock or reconciliation pops.
   */
  scale(now) {
    const t = now - this.at;
    const { slowSec, slowScale, recoverSec } = KILL_CONFIRM;
    if (t < 0 || t >= slowSec + recoverSec) return 1;
    if (t < slowSec) return slowScale;
    const k = (t - slowSec) / recoverSec; // 0..1 easing home
    return slowScale + (1 - slowScale) * (k * k * (3 - 2 * k));
  }

  // 0..1 envelope for the camera pull: quick in, ease out over the slow window.
  _pull(now) {
    const t = now - this.at;
    const total = KILL_CONFIRM.slowSec + KILL_CONFIRM.recoverSec;
    if (t < 0 || t >= total || !this.hasVictim) return 0;
    const inK = Math.min(1, t / 0.12);
    const outK = 1 - t / total;
    return inK * outK * outK;
  }

  /**
   * Nudge the already-posed camera toward the victim and punch the FOV.
   * Stateless per frame: the chase cam re-poses the camera each frame, then
   * this rotates it by at most pullMaxRad·envelope, so nothing accumulates.
   * Scoped gunsights own their FOV — the punch only touches the wide view.
   */
  applyCamera(camera, setFov, now) {
    const k = this._pull(now);
    // Scoped gunsights (naval/land zoom) own their camera — leave them alone.
    if (k <= 0 || camera.fov < 70) return;
    _fwd.set(0, 0, -1).applyQuaternion(camera.quaternion);
    _toV.copy(this.victimPos).sub(camera.position);
    const len = _toV.length();
    if (len < 1e-3) return;
    _toV.multiplyScalar(1 / len);
    if (_fwd.dot(_toV) <= 0) return; // victim behind the camera: no pull
    _lookM.lookAt(camera.position, this.victimPos, camera.up);
    _lookQ.setFromRotationMatrix(_lookM);
    camera.quaternion.rotateTowards(_lookQ, KILL_CONFIRM.pullMaxRad * k);
    setFov(72 + KILL_CONFIRM.fovPunchDeg * k);
  }
}
